import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  enableFirmwareMfaSetup,
  firmwareList,
  getFirmwareMfaStatus,
  sendFirmwareMfaSetupEmail,
  verifyFirmwareMfaSetup,
  verifyFirmwareMfaSetupEmail,
} from "../../actions/doctorActions";
import Table from "../react-table-component/ReactTable";
import { formatDate } from "../../helper";
import { Link } from "react-router-dom";
import { DropDownItem } from "../header/Header";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import historyIcon from "../../assets/img/svg/history-icon.svg";
import editIcon from "../../assets/img/svg/edit-icon.svg";
import dotMenuIcon from "../../assets/img/svg/dots-menu-icon.svg";
import trashIcon from "../../assets/img/svg/trash-can-2.svg";
import downloadIcon from "../../assets/img/svg/download.svg";
import { FirmwareDeleteConfirmModal } from "./FirmwareDeleteConfirmModal";

export default function FirmwareList() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const { loading, firmware } = useSelector((state) => state.firmwareList);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [id, setId] = useState(null);
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);
  const [isVerifyingEmailCode, setIsVerifyingEmailCode] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailVerifyError, setEmailVerifyError] = useState("");
  const [showMfaSetupPanel, setShowMfaSetupPanel] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState("");
  const [mfaSetupError, setMfaSetupError] = useState("");
  const [isConfirmingMfaSetup, setIsConfirmingMfaSetup] = useState(false);

  const [searchField] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 100 : prev + 20));
      }, 400);
      return () => clearInterval(interval);
    };
    simulateProgress();
    dispatch(firmwareList(token)).finally(() => setProgress(100));
  }, [dispatch, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    dispatch(getFirmwareMfaStatus(token))
      .then((response) => {
        setIsMfaEnabled(!!response?.data?.mfa_enabled);
      })
      .catch(() => {
        setIsMfaEnabled(false);
      });
  }, [dispatch, token]);

  const filteredFirmware = React.useMemo(() => {
    return (
      firmware?.filter((item) =>
        item.firmware_version?.toLowerCase()?.includes(searchField),
      ) || []
    );
  }, [firmware, searchField]);

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);
  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const handleDownload = (fw) => {
    window.open(fw.path_to_folder, "_blank");
  };

  const handleStartAuthenticatorFlow = async () => {
    setIsSendingEmailVerification(true);
    setEmailVerifyError("");

    try {
      await dispatch(sendFirmwareMfaSetupEmail(token));
      setEmailCode("");
      setShowEmailVerifyModal(true);
      toast.success("Verification email sent. Enter the 6-digit code to continue.");
    } catch (error) {
      toast.error(error.message || "Unable to send verification email.");
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handleCloseEmailVerifyModal = () => {
    if (isVerifyingEmailCode) {
      return;
    }
    setShowEmailVerifyModal(false);
    setEmailCode("");
    setEmailVerifyError("");
  };

  const handleVerifyEmailAndStartSetup = async () => {
    if (!emailCode.trim()) {
      setEmailVerifyError("Enter the 6-digit verification code from your email.");
      return;
    }

    setIsVerifyingEmailCode(true);
    setEmailVerifyError("");

    try {
      await dispatch(verifyFirmwareMfaSetupEmail(emailCode.trim(), token));
      const setupResponse = await dispatch(enableFirmwareMfaSetup(token));
      setMfaSetupData(setupResponse.data || null);
      setMfaSetupCode("");
      setMfaSetupError("");
      setShowMfaSetupPanel(true);
      setShowEmailVerifyModal(false);
      toast.success("Authenticator setup started. Scan the QR code and verify.");
    } catch (error) {
      setEmailVerifyError(error.message || "Email verification failed.");
    } finally {
      setIsVerifyingEmailCode(false);
    }
  };

  const handleConfirmMfaSetup = async () => {
    if (!mfaSetupCode.trim()) {
      setMfaSetupError("Enter the 6-digit code from Google Authenticator.");
      return;
    }

    setIsConfirmingMfaSetup(true);
    setMfaSetupError("");

    try {
      await dispatch(verifyFirmwareMfaSetup(mfaSetupCode.trim(), token));
      setShowMfaSetupPanel(false);
      setMfaSetupData(null);
      setMfaSetupCode("");
      setIsMfaEnabled(true);
      toast.success("Authenticator setup completed.");
    } catch (error) {
      setMfaSetupError(error.message || "Authenticator setup verification failed.");
    } finally {
      setIsConfirmingMfaSetup(false);
    }
  };

  const columns = React.useMemo(
    () => [
      { Header: "Firmware Version", accessor: "firmware_version" },
      {
        Header: "Date Uploaded",
        accessor: "date_uploaded",
        Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
      },
      {
        Header: "Changelog",
        accessor: "changelog",
        Cell: ({ value }) =>
          value && value.length > 60
            ? `${value.slice(0, 60)}...`
            : value || "N/A",
      },
      { Header: "File Size (Bytes)", accessor: "file_size" },
      { Header: "IOS Version", accessor: "version" },
      {
        Header: "",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isMenuOpen = openMenuRowIndex === row.index;
          return (
            <div className="dot-menu-icon">
              <img
                src={dotMenuIcon}
                alt="dot menu"
                onClick={() => toggleMenuForRow(row.index)}
              />

              {isMenuOpen && (
                <div ref={dotMenuRef} className="patients-dot-menu">
                  <DropDownItem
                    item={{
                      title: "View",
                      icon: historyIcon,
                      to: `/firmware/${row?.original?.id}`,
                    }}
                  />
                  <DropDownItem
                    item={{
                      title: "Edit",
                      icon: editIcon,
                      to: `/firmware-update/${row?.original?.id}`,
                    }}
                  />
                  <DropDownItem
                    item={{
                      title: "Download",
                      icon: downloadIcon,
                    }}
                    handleClick={() => handleDownload(row?.original)}
                  />
                  <div className="delete-clr">
                    <DropDownItem
                      item={{
                        title: "Delete",
                        icon: trashIcon,
                      }}
                      handleClick={() => {
                        setId(row?.original?.id);
                        setShowDeleteDialog(true);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowIndex]
  );

  return (
    <div className="patient-list">
      <FirmwareDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false);
        }}
        id={id}
        token={token}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0 }}>Firmware List</h3>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className="button btn-default"
            style={{
              padding: "0.35rem 0.7rem",
              fontSize: "0.9rem",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handleStartAuthenticatorFlow}
            disabled={
              isSendingEmailVerification ||
              isVerifyingEmailCode ||
              isConfirmingMfaSetup
            }
          >
            {isSendingEmailVerification
              ? "Sending Email..."
              : "Setup Authenticator"}
          </button>
          <Link to={`/firmware-create`}>
            <button
              className="button btn-default"
              style={{
                padding: "0.35rem 0.7rem",
                fontSize: "0.9rem",
                fontWeight: "bold",
              }}
            >
              Add Firmware
            </button>
          </Link>
        </div>
      </div>

      <div className="mb-3">
        <small className={isMfaEnabled ? "text-success" : "text-muted"}>
          {isMfaEnabled
            ? "Google Authenticator is currently configured for this account."
            : "Google Authenticator is not configured for this account."}
        </small>
      </div>

      {showMfaSetupPanel && (
        <div className="mb-4 p-3 border rounded">
          <h5 className="mb-3">Google Authenticator Setup</h5>
          {mfaSetupData?.qr_code ? (
            <div className="mb-3">
              <img
                src={mfaSetupData.qr_code}
                alt="Authenticator QR Code"
                style={{ maxWidth: "220px", width: "100%" }}
              />
            </div>
          ) : null}
          {mfaSetupData?.manual_secret ? (
            <div className="mb-3">
              <strong>Manual Secret:</strong> {mfaSetupData.manual_secret}
            </div>
          ) : null}
          <div className="mb-2">
            <input
              type="text"
              className="form-control login-form-input"
              value={mfaSetupCode}
              maxLength={6}
              onChange={(event) => {
                const normalizedValue = event.target.value.replace(/\D/g, "");
                setMfaSetupCode(normalizedValue);
                setMfaSetupError("");
              }}
              placeholder="Enter 6-digit code"
              disabled={isConfirmingMfaSetup}
            />
          </div>
          {mfaSetupError ? <div className="text-danger mb-2">{mfaSetupError}</div> : null}
          <button
            type="button"
            className="btn btn-sm text-light main-bg btn-style"
            onClick={handleConfirmMfaSetup}
            disabled={isConfirmingMfaSetup || !mfaSetupData}
          >
            <b>{isConfirmingMfaSetup ? "Verifying Setup..." : "Verify Setup"}</b>
          </button>
        </div>
      )}

      <Table
        columns={columns}
        data={filteredFirmware}
        loading={loading}
        tableOf="Firmware"
        progress={progress}
      />

      <Modal show={showEmailVerifyModal} onHide={handleCloseEmailVerifyModal} centered>
        <Modal.Header closeButton={!isVerifyingEmailCode}>
          <Modal.Title>Verify Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Enter the 6-digit verification code sent to your registered email before
            setting up Google Authenticator.
          </p>
          <input
            type="text"
            className="form-control login-form-input"
            value={emailCode}
            maxLength={6}
            onChange={(event) => {
              const normalizedValue = event.target.value.replace(/\D/g, "");
              setEmailCode(normalizedValue);
              setEmailVerifyError("");
            }}
            placeholder="123456"
            disabled={isVerifyingEmailCode}
          />
          {emailVerifyError ? <div className="text-danger mt-2">{emailVerifyError}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-sm text-light btn-style"
            onClick={handleCloseEmailVerifyModal}
            disabled={isVerifyingEmailCode}
          >
            <b>Cancel</b>
          </button>
          <button
            type="button"
            className="btn btn-sm text-light main-bg btn-style"
            onClick={handleVerifyEmailAndStartSetup}
            disabled={isVerifyingEmailCode}
          >
            <b>{isVerifyingEmailCode ? "Verifying..." : "Verify Email"}</b>
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
